import {
  GridActionsColDef,
  GridColDef,
  GridRenderCellParams,
  GridRowParams
} from '@mui/x-data-grid-premium';
import { currencyFormatter } from 'components/helpers/currency-formatter';
import { useIsTenantAdministrator } from 'shared/permissions/IsTenantAdministrator';
import HistoryDateTimeUpdateDialog from 'features/settings/history/components/HistoryDateTimeUpdateDialog';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { baseCell, dateTimeCell, DataGridCell } from 'components/cells';
import {
  filterOperators,
  dateFilterOperators,
  numberFilterOperators,
  singleSelectFilterOperators
} from 'components/enhanced';
import { ConfirmationDeleteTableDialog } from 'components/ui/feedback';

export const ExamHistoryLineColumns = (
  handleDateTimeUpdate: any,
  handleDelete: any,
  hideFields: boolean,
  tagOptions?: string[],
  macroExamOptions?: string[]
): GridColDef[] => {
  const translationPrefix = 'entities.exam';

  const { t } = useTranslation();
  const columns: GridColDef[] = [];

  columns.push(
    {
      field: 'actions',
      type: 'actions',
      editable: false,
      headerName: t(`global.actions`),
      getActions: (params: GridRowParams) => {
        const actions = [];
        if (useIsTenantAdministrator()) {
          actions.push(
            <HistoryDateTimeUpdateDialog
              key="datetime-update"
              id={params.row.id}
              handleUpdate={handleDateTimeUpdate}
            />
          );
          actions.push(
            <ConfirmationDeleteTableDialog
              key="delete-dialog"
              handleDelete={handleDelete}
              id={params.row.id}
            />
          );
        }
        return actions;
      }
    } as GridActionsColDef,
    {
      field: 'exam',
      headerName: t(`${translationPrefix}.history.exam`),
      width: 100,
      filterOperators,
      editable: false,
      renderCell: baseCell
    },
    {
      field: 'datetime',
      headerName: t(`${translationPrefix}.history.datetime`),
      width: 150,
      filterOperators: dateFilterOperators,
      renderCell: dateTimeCell,
      valueFormatter: (params) => {
        return params.value ? dayjs(params.value).format('YYYY-MM-DD HH:MM:00') : '';
      }
    },
    {
      field: 'category',
      headerName: t(`${translationPrefix}.category`),
      width: 100,
      filterOperators,
      renderCell: baseCell
    },
    {
      field: 'regional_rate_snapshot_code',
      headerName: t(`${translationPrefix}.regional_rate_snapshot_code`),
      width: 100,
      filterOperators,
      renderCell: baseCell
    }
  );

  [
    'regional_rate_snapshot_rate',
    'contractual_rate',
    'rate_1',
    'rate_2',
    'rate_3',
    'rate_4',
    'rate_5'
  ].forEach((field) => {
    columns.push({
      field: field,
      width: 150,
      headerName: t(`${translationPrefix}.${field}`),
      filterOperators: numberFilterOperators,
      sortable: true,
      renderCell: (params: GridRenderCellParams) => {
        const price = params.value;
        return <DataGridCell content={currencyFormatter(price)} />;
      }
    });
  });

  ['extra_1', 'extra_2', 'extra_3', 'extra_4', 'extra_5'].forEach((field) => {
    columns.push({
      field: field,
      width: 200,
      headerName: t(`${translationPrefix}.${field}`),
      filterOperators,
      sortable: true,
      renderCell: baseCell
    });
  });

  // Tag column
  const tagColumn: GridColDef = {
    field: 'tag',
    width: 100,
    headerName: t(`${translationPrefix}.history.tag`),
    sortable: true,
    renderCell: baseCell,
    filterOperators: singleSelectFilterOperators,
    ...(tagOptions && tagOptions.length > 0
      ? {
          type: 'singleSelect',
          valueOptions: tagOptions
        }
      : {
          filterOperators
        })
  };

  // Macro exam column
  const macroExamColumn: GridColDef = {
    field: 'macro_exam',
    width: 125,
    headerName: t(`${translationPrefix}.history.macro_exam`),
    sortable: true,
    renderCell: baseCell,
    filterOperators: singleSelectFilterOperators,
    ...(macroExamOptions && macroExamOptions.length > 0
      ? {
          type: 'singleSelect',
          valueOptions: macroExamOptions
        }
      : {
          filterOperators
        })
  };

  // Tag column with select filter
  // const tagColumn: GridColDef = {
  //   field: 'tag',
  //   width: 125,
  //   headerName: t(`${translationPrefix}.history.tag`),
  //   sortable: true,
  //   filterable: true,
  //   renderCell: baseCell
  // };

  // if (tagOptions && tagOptions.length > 0) {
  //   tagColumn.type = 'singleSelect';
  //   tagColumn.valueOptions = tagOptions;
  // } else {
  //   tagColumn.filterOperators = filterOperators;
  // }

  columns.push(tagColumn);

  // Macro exam column with select filter
  // const macroExamColumn: GridColDef = {
  //   field: 'macro_exam',
  //   width: 125,
  //   headerName: t(`${translationPrefix}.history.macro_exam`),
  //   sortable: true,
  //   renderCell: baseCell
  // };

  // if (macroExamOptions && macroExamOptions.length > 0) {
  //   macroExamColumn.type = 'singleSelect';
  //   macroExamColumn.valueOptions = macroExamOptions;
  // } else {
  //   macroExamColumn.filterOperators = filterOperators;
  // }

  columns.push(macroExamColumn);

  if (!hideFields) {
    ['products_snapshot', 'equipments_snapshot', 'workstations_snapshot'].forEach((field) => {
      columns.push({
        field: field,
        width: 150,
        headerName: t(`${translationPrefix}.history.${field}`),
        sortable: false,
        valueFormatter: ({ value }) => {
          const values = value.map((value) => value.code);
          return values.join(', ');
        },
        filterOperators: filterOperators,
        renderCell: (params: GridRenderCellParams) => {
          const value = params.value.map((value) => value['code']);
          return <DataGridCell content={value ? value.join(',') : ''} />;
        }
      });
    });
  }

  return columns;
};
